import React, { useState } from "react";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import { StyledTableCellForName, StyledTableCellForStatus } from "./styles";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import UpdateIcon from "@mui/icons-material/Update";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import TableRow from "@mui/material/TableRow";
import ReAuthDialog from "../auth/ReAuthDialog";
import {
    downloadCertificate,
    getPrivateKey,
    deletePrivateKey,
    uploadPrivateKey,
    updatePrivateKey,
} from "../../api/requests";
import * as zip from "@zip.js/zip.js";
import { useSelector } from "react-redux";
import KeyModal from "./keyModal";
import Tooltip from "@mui/material/Tooltip";

const Body = (props) => {
    const isAdmin = useSelector((state) => state.auth.isAdmin);

    const { data, rowsPerPage, page, emptyRows } = props;

    const [isDownloaded, setIsDownloaded] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(false);

    const [open, setOpen] = useState(false);
    const [row, setRow] = useState(false);

    const [openKey, setOpenKey] = useState(false);
    const [openGetKey, setOpenGetKey] = useState(false);
    const [openDeleteKey, setOpenDeleteKey] = useState(false);
    const [openUpdateKey, setOpenUpdateKey] = useState(false);

    const [dataState, setDataState] = useState(data);

    const getNameForCert = () => {
        return row.commonName.charAt(0) === "*"
            ? "_" + row.commonName.substring(1, row.commonName.length)
            : row.commonName;
    };

    const zipCertificate = async (certificateData) => {
        // - create the inputBlob object storing the data to compress
        const inputCertificateBlob = new Blob(
            [certificateData.pems.certificate],
            {
                type: "text/plain",
            }
        );

        const inputChainBlob = new Blob(
            [
                `${certificateData.pems.intermediate}\n${certificateData.pems.root}`,
            ],
            { type: "text/plain" }
        );

        // - create a Data64URIWriter object to write the zipped data into a data URI
        // - create a ZipWriter object with the Data64URIWriter object as parameter
        const zipWriter = new zip.ZipWriter(
            new zip.Data64URIWriter("application/zip")
        );

        // - create a BlobReader object to read the content of inputBlob
        // - add a new file named "text.txt" in the zip and associate it to the BlobReader object
        await zipWriter.add(
            `${getNameForCert()}.crt`,
            new zip.BlobReader(inputCertificateBlob)
        );
        await zipWriter.add(
            `${getNameForCert()}.chain.crt`,
            new zip.BlobReader(inputChainBlob)
        );

        // - close the ZipWriter object and get compressed data
        const dataURI = await zipWriter.close();
        // data:application/zip;base64,UEsDBBQACAgIAJuFO1IAAAAAAAAAAAAAAAAIAAAAdGVzdC50e...
        return dataURI;
    };

    const saveCertificateToFile = async (certificateData) => {
        const zipData = await zipCertificate(certificateData);
        const a = document.createElement("a");
        a.href = zipData;
        a.setAttribute("download", getNameForCert() + ".zip");
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const handleDownloadCertificate = async () => {
        setIsDownloaded(true);
        setSelectedIndex(row.index);
        const certificateData = await downloadCertificate(row.firestoreId);
        if (certificateData) {
            await saveCertificateToFile(certificateData);
        }
        setIsDownloaded(false);
        setSelectedIndex(false);
        setRow(false);
    };

    const savePrivateKeyToFile = (data, filename, type) => {
        var file = new Blob([data], { type: type });
        if (window.navigator.msSaveOrOpenBlob)
            // IE10+
            window.navigator.msSaveOrOpenBlob(file, filename);
        else {
            // Others
            var a = document.createElement("a"),
                url = URL.createObjectURL(file);
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            setTimeout(function () {
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }, 0);
        }
    };

    const handleDownloadPrivateKey = async () => {
        setIsDownloaded(true);
        setSelectedIndex(row.index);
        const privateKeyData = await getPrivateKey(row.firestoreId);
        if (privateKeyData) {
            savePrivateKeyToFile(
                privateKeyData.key,
                getNameForCert() + ".key",
                "text/plain"
            );
        }
        setIsDownloaded(false);
        setSelectedIndex(false);
        setRow(false);
    };

    const handleDeletePrivateKey = async () => {
        setIsDownloaded(true);
        setSelectedIndex(row.index);
        const privateKeyData = await deletePrivateKey(row.firestoreId);
        if (privateKeyData) {
            setDataState((prev) => {
                return prev.map((cert) => {
                    if (cert.firestoreId === row.firestoreId) {
                        return { ...cert, hasKey: false };
                    }
                    return cert;
                });
            });
        }
        setIsDownloaded(false);
        setSelectedIndex(false);
        setRow(false);
    };
    return (
        <TableBody data-testid="table-body">
            <TableRow style={{ visibility: "collapse" }}>
                <TableCell>
                    {/* ReAuthDialog was wrapped inside the table row and cell due to console error which not allowed div inside a table */}
                    <ReAuthDialog
                        open={open}
                        setOpen={setOpen}
                        handler={handleDownloadCertificate}
                    />
                </TableCell>
            </TableRow>
            <TableRow style={{ visibility: "collapse" }}>
                <TableCell>
                    <KeyModal
                        open={openKey}
                        setOpen={setOpenKey}
                        row={row}
                        setDataState={setDataState}
                        callback={uploadPrivateKey}
                    />
                </TableCell>
            </TableRow>
            <TableRow style={{ visibility: "collapse" }}>
                <TableCell>
                    <KeyModal
                        open={openUpdateKey}
                        setOpen={setOpenUpdateKey}
                        row={row}
                        setDataState={setDataState}
                        callback={updatePrivateKey}
                        isUpdate={true}
                    />
                </TableCell>
            </TableRow>
            <TableRow style={{ visibility: "collapse" }}>
                <TableCell>
                    {/* ReAuthDialog was wrapped inside the table row and cell due to console error which not allowed div inside a table */}
                    <ReAuthDialog
                        open={openGetKey}
                        setOpen={setOpenGetKey}
                        handler={handleDownloadPrivateKey}
                        dialogSuffix={"-03"}
                    />
                </TableCell>
            </TableRow>
            <TableRow style={{ visibility: "collapse" }}>
                <TableCell>
                    {/* ReAuthDialog was wrapped inside the table row and cell due to console error which not allowed div inside a table */}
                    <ReAuthDialog
                        open={openDeleteKey}
                        setOpen={setOpenDeleteKey}
                        handler={handleDeletePrivateKey}
                        dialogSuffix={"-04"}
                    />
                </TableCell>
            </TableRow>
            {(rowsPerPage > 0
                ? dataState.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                  )
                : dataState
            ).map((row) => (
                <TableRow key={row?.index}>
                    <TableCell component="th" scope="row">
                        {row?.index}
                    </TableCell>
                    <StyledTableCellForName component="th" scope="row">
                        {row?.commonName}
                    </StyledTableCellForName>
                    <TableCell component="th" scope="row">
                        {row?.serialNumber?.toString()}
                    </TableCell>
                    <TableCell style={{ width: 160 }} align="center">
                        {row?.timeToExpire}
                    </TableCell>
                    <StyledTableCellForStatus
                        rowstatus={row?.status}
                        style={{ width: 160 }}
                        align="center"
                    >
                        {row?.status ? row.status : ""}
                    </StyledTableCellForStatus>
                    {isAdmin && (
                        <TableCell component="th" scope="row" align="center">
                            <>
                                {row?.status === "ISSUED" && row?.hasKey && (
                                    <LoadingButton
                                        onClick={() => {
                                            setOpenGetKey(true);
                                            setRow(row);
                                        }}
                                        loading={
                                            selectedIndex === row.index &&
                                            isDownloaded
                                        }
                                        loadingPosition="start"
                                        startIcon={
                                            <Tooltip title="Save">
                                                <SaveIcon
                                                    sx={{
                                                        mr: "-10px",
                                                    }}
                                                />
                                            </Tooltip>
                                        }
                                        variant="contained"
                                        size="small"
                                        data-testid={`save-key-btn-${row.index}`}
                                        sx={{
                                            // m: 1,
                                            // p: "2px",
                                            minWidth: "30px",
                                        }}
                                    ></LoadingButton>
                                )}
                                {row?.status === "ISSUED" && !row?.hasKey && (
                                    <LoadingButton
                                        onClick={() => {
                                            setOpenKey(true);
                                            setRow(row);
                                        }}
                                        loading={
                                            selectedIndex === row.index &&
                                            isDownloaded
                                        }
                                        loadingPosition="start"
                                        startIcon={
                                            <Tooltip title="Upload">
                                                <UploadFileIcon
                                                    sx={{
                                                        mr: "-10px",
                                                    }}
                                                />
                                            </Tooltip>
                                        }
                                        variant="contained"
                                        size="small"
                                        data-testid={`upload-btn-${row.index}`}
                                        sx={{
                                            ml: 1,
                                            // pr: "2px",
                                            minWidth: "30px",
                                            backgroundColor: "#03b6fc",
                                        }}
                                    ></LoadingButton>
                                )}
                                {row?.status === "ISSUED" && row?.hasKey && (
                                    <LoadingButton
                                        onClick={() => {
                                            setOpenUpdateKey(true);
                                            setRow(row);
                                        }}
                                        loading={
                                            selectedIndex === row.index &&
                                            isDownloaded
                                        }
                                        loadingPosition="start"
                                        startIcon={
                                            <Tooltip title="Update">
                                                <UpdateIcon
                                                    sx={{
                                                        mr: "-10px",
                                                    }}
                                                />
                                            </Tooltip>
                                        }
                                        variant="contained"
                                        size="small"
                                        data-testid={`update-btn-${row.index}`}
                                        sx={{
                                            ml: 1,
                                            // pr: "2px",
                                            minWidth: "30px",
                                            backgroundColor: "#03b6fc",
                                        }}
                                    ></LoadingButton>
                                )}
                                {row?.status === "ISSUED" && row?.hasKey && (
                                    <LoadingButton
                                        onClick={() => {
                                            setOpenDeleteKey(true);
                                            setRow(row);
                                        }}
                                        loading={
                                            selectedIndex === row.index &&
                                            isDownloaded
                                        }
                                        loadingPosition="start"
                                        startIcon={
                                            <Tooltip title="Delete">
                                                <DeleteForeverIcon
                                                    sx={{
                                                        mr: "-10px",
                                                    }}
                                                />
                                            </Tooltip>
                                        }
                                        variant="contained"
                                        size="small"
                                        data-testid={`delete-btn-${row.index}`}
                                        sx={{
                                            ml: 1,
                                            // pr: "2px",
                                            minWidth: "30px",
                                            backgroundColor: "#c90e0e",
                                        }}
                                    ></LoadingButton>
                                )}
                            </>
                        </TableCell>
                    )}
                    <TableCell component="th" scope="row" align="center">
                        {row?.status === "ISSUED" && (
                            <LoadingButton
                                onClick={() => {
                                    setOpen(true);
                                    setRow(row);
                                }}
                                loading={
                                    selectedIndex === row.index && isDownloaded
                                }
                                loadingPosition="start"
                                startIcon={<SaveIcon />}
                                variant="contained"
                                size="small"
                                data-testid={`save-btn-${row.index}`}
                            >
                                Save
                            </LoadingButton>
                        )}
                    </TableCell>
                </TableRow>
            ))}

            {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={6} />
                </TableRow>
            )}
        </TableBody>
    );
};

export default Body;
